import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useChatStore, useFixChatTimestamps } from './stores/chatStore';
import ChatMessage from './components/ChatMessage';
import { ChatHeader } from './components/ChatHeader';
import { MessageList } from './components/MessageList';
import { ChatInputForm } from './components/ChatInputForm';
import { EmailCaptureModal } from './components/EmailCaptureModal';
import { EndChatButton } from './components/EndChatButton';
import { DebugPanel } from './components/DebugPanel';
import './index.css';
import { ChatConfig } from './types';
import { getBrandingColor, getBrandingFont, loadGoogleFonts } from './utils/brandingUtils';
import { clearStorageIfNeeded } from './utils/storageUtils';
import { useBranding } from './hooks/useBranding';
import { useAutoScroll } from './hooks/useAutoScroll';
import { useChatInitialization } from './hooks/useChatInitialization';
import { useEmbeddedEvents } from './hooks/useEmbeddedEvents';
import { usePageUnloadHandler } from './hooks/usePageUnloadHandler';

// ========== Constants ==========
/**
 * Default branding colors used when no custom branding is available
 */
const DEFAULT_COLORS = {
  primary: '#3b82f6',
  secondary: '#6b7280',
  background: '#f5f5f5',
  text: '#333333'
};

// ========== Types ==========
/**
 * Available color keys for branding customization
 */
type ColorKey = 'primary' | 'secondary' | 'background' | 'text';

/**
 * Available font types for branding customization
 */
type FontType = 'primary' | 'secondary';

// ========== Utility Functions ==========

/**
 * Main App Component
 * Handles the chat interface, message sending, branding, and session management
 */
function App() {
  // ========== State Hooks ==========
  /**
   * Current message being composed by the user
   */
  const [message, setMessage] = useState('');
  
  /**
   * Whether the user is currently composing a message (for IME input)
   */
  const [isComposing, setIsComposing] = useState(false);
  
  /**
   * Whether to show the email capture form
   */
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  
  /**
   * Email entered by the user
   */
  const [email, setEmail] = useState('');
  
  /**
   * Whether the email capture form has been submitted
   */
  const [captureSubmitted, setCaptureSubmitted] = useState(false);
  
  /**
   * Reference to the end of the messages container for auto-scrolling
   */
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  /**
   * Reference to the input field for focus management
   */
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // ========== Store Access ==========
  const { 
    messages, 
    isLoading, 
    error,
    config,
    threadId,
    sessionId,
    addMessage,
    sendMessage,
    fetchConfig,
    endSession,
    handleProductSelect,
    resetSessionTimer,
    startSession,
    actualMerchantId,
    clearMessages
  } = useChatStore();
  
  /**
   * Force a reload of the configuration with debug flags
   */
  const forceReloadConfig = useCallback(() => {
    // Clear any cached config
    sessionStorage.removeItem('mattressai-chat-store');
    
    // Add debug parameters to the URL
    const url = new URL(window.location.href);
    url.searchParams.set('debug', 'true');
    url.searchParams.set('v', Date.now().toString());
    
    // Replace the URL without refreshing
    window.history.replaceState({}, '', url.toString());
    
    // Fetch the config using the store function
    useChatStore.getState().fetchConfig();
    
    // Log the action
    console.log('[MattressAI Debug] Forcing config reload at', new Date().toISOString());
  }, []);
  
  // ========== Custom Hooks ==========
  // Handle initialization, returns debug state
  const { showDebug, setShowDebug, debugSessionId } = useChatInitialization();
  
  // Apply branding based on config
  useBranding(config);
  
  // Automatically scroll to bottom when messages/loading state change
  useAutoScroll(messagesEndRef, [messages, isLoading]);
  
  // Listen for events from parent window (e.g., END_CHAT)
  useEmbeddedEvents(endSession, actualMerchantId);
  
  // Handle page unload logic (ending session, notifying parent)
  usePageUnloadHandler({
    sessionId,
    threadId,
    messagesLength: messages.length,
    resetSessionTimer,
    endSession,
  });
  
  // Handle page refresh to clear chat interface
  useEffect(() => {
    // Check if this is a page refresh
    const isPageRefresh = () => {
      // Use performance API to detect page refresh
      if (window.performance) {
        const entries = performance.getEntriesByType('navigation');
        if (entries.length > 0 && entries[0] instanceof PerformanceNavigationTiming) {
          return (entries[0] as PerformanceNavigationTiming).type === 'reload';
        }
        
        // Fallback for older browsers
        if (window.performance.navigation) {
          return window.performance.navigation.type === 1; // 1 = reload
        }
      }
      return false;
    };
    
    // Use sessionStorage to track page loads
    const pageLoadTimestamp = sessionStorage.getItem('page-load-timestamp');
    const currentTimestamp = Date.now().toString();
    
    // If we have a previous timestamp, this is likely a refresh
    if (pageLoadTimestamp) {
      console.log('[MattressAI] Page refresh detected, clearing messages');
      clearMessages(); // Clear messages but keep session alive
      
      // Add the initial greeting message
      if (config && config.chatConfig && config.chatConfig.initialGreeting) {
        const initialMessage = {
          id: `welcome_${Date.now()}`,
          content: config.chatConfig.initialGreeting,
          role: 'assistant' as const,
          timestamp: new Date()
        };
        
        // Add the initial greeting with a small delay to ensure it appears after clearing
        setTimeout(() => {
          addMessage(initialMessage);
        }, 100);
      }
    }
    
    // Update the timestamp for next refresh detection
    sessionStorage.setItem('page-load-timestamp', currentTimestamp);
    
  }, [clearMessages, config, addMessage]); // Include config and addMessage in dependencies
  
  // ========== UI Handlers ==========
  /**
   * Handles form submission when user sends a message
   * @param e - The form submission event
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim() || isLoading || isComposing) return;
    
    const currentMessage = message;
    setMessage('');
    await handleSendMessage(currentMessage);
  };
  
  /**
   * Sends a user message to the chat API
   * @param content - The message content to send
   */
  const handleSendMessage = async (content: string) => {
    try {
      await sendMessage(content);
    } catch (error) {
      // Errors are already handled in the store
    }
  };
  
  /**
   * Initiates the end chat sequence, showing the email capture modal
   */
  const handleEndChat = () => {
    setShowEmailCapture(true);
  };
  
  /**
   * Handles the email capture form submission
   * Sends the email as a hidden message and ends the session
   * @param e - The form submission event
   */
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) return;
    
    // Send the email as a hidden message to capture it in the session
    await handleSendMessage(`email:${email.trim()}`);
    
    // Mark as submitted
    setCaptureSubmitted(true);
    
    // End the session after a short delay to ensure the email is processed
    setTimeout(() => {
      endSession('email_captured');
    }, 500);
  };
  
  /**
   * Clears all local storage and session storage, then reloads the page
   * Used for debugging and troubleshooting
   */
  const handleClearCache = useCallback(() => {
    // Clear all storage
    localStorage.removeItem('mattressai-chat-store');
    localStorage.removeItem('last-hostname');
    sessionStorage.removeItem('loaded-fonts');
    sessionStorage.removeItem('lastActivityTime');
    sessionStorage.removeItem('page-load-timestamp');
    
    // Force reload the page
    window.location.reload();
  }, []);
  
  // ========== Rendering ==========
  /**
   * Render loading screen when initializing with no messages
   */
  if (isLoading && messages.length === 0) {
    return (
      <div className="flex flex-col h-screen" style={{ backgroundColor: getBrandingColor(config, 'background') }}>
        <ChatHeader config={config} />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-blue-200 mb-2"></div>
            <div className="h-4 w-24 bg-blue-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }
  
  /**
   * Main chat interface rendering
   */
  return (
    <div 
      className="flex flex-col h-screen overflow-hidden"
      style={{ backgroundColor: getBrandingColor(config, 'background') }}
    >
      {/* Header */}
      <ChatHeader config={config} />
      
      {/* Message Area */}
      <div className="flex-1 overflow-hidden relative">
        <MessageList 
          messages={messages}
          isLoading={isLoading}
          error={error}
          config={config}
          messagesEndRef={messagesEndRef}
          handleProductSelect={handleProductSelect}
          endSession={endSession}
          handleSendMessage={handleSendMessage}
        />
      </div>
      
      {/* Email Capture Modal */}
      {showEmailCapture && (
        <EmailCaptureModal
          email={email}
          setEmail={setEmail}
          captureSubmitted={captureSubmitted}
          handleEmailSubmit={handleEmailSubmit}
          setShowEmailCapture={setShowEmailCapture}
          endSession={endSession}
          config={config}
        />
      )}
      
      {/* Input Form */}
      <div className="p-2 md:p-4 border-t border-gray-200 bg-white">
        <ChatInputForm 
          inputRef={inputRef}
          message={message}
          setMessage={setMessage}
          isComposing={isComposing}
          setIsComposing={setIsComposing}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
          config={config}
        />
        
        {/* End Chat Button */}
        <EndChatButton onClick={handleEndChat} />
      </div>
      
      {/* Debug Panel */}
      {showDebug && (
        <div className="debug-panel">
          <DebugPanel 
            messages={messages} 
            sessionId={sessionId} 
            threadId={threadId} 
            config={config}
            debugSessionId={debugSessionId}
          />
          <button 
            onClick={forceReloadConfig}
            className="bg-red-600 text-white px-3 py-1 rounded text-sm mt-2"
          >
            Force Config Reload
          </button>
        </div>
      )}
    </div>
  );
}

export default App; 